import React from 'react';
import { useState } from 'react';
import { Piano, Users, Award, Music2, Sparkles, Volume2, X, Phone, Mail, Hand, Clock } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PianoQueueDroitNeufs = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();
  const scrollRef4 = useScrollAnimation();
  const [selectedPiano, setSelectedPiano] = useState<any>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const pianos = [
    {
      brand: "WILHELM STEINBERG",
      image: "/steinberg.jpg",
      features: ["Design moderne", "Excellent rapport qualité-prix", "Sonorité riche", "Fiabilité éprouvée"],
      specs: {
        gamme: "P, AT, IQ Series",
        tailles: "Droits 108-125cm, Queues 148-186cm",
        caracteristiques: "Mécanique européenne premium",
        finitions: "Ébène, Noyer, Blanc",
        origine: "Europe - Standards allemands",
        avantages: "Qualité professionnelle accessible"
      }
    },
    {
      brand: "KAWAI",
      image: "/imageskawai.jpeg",
      features: ["Tradition japonaise", "Innovation technologique", "Toucher exceptionnel", "Qualité reconnue mondialement"],
      specs: {
        gamme: "K Series, GL Series",
        tailles: "Droits 114-132cm, Queues 165-227cm",
        caracteristiques: "Mécanique Millennium III, touches bois",
        finitions: "Ébène poli, Acajou, Blanc brillant",
        origine: "Japon - Partenaire depuis des décennies",
        reputation: "Récompensé par de nombreux prix internationaux"
      }
    },
    {
      brand: "YAMAHA",
      image: "https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=800",
      features: ["Leader mondial", "Qualité constante", "Polyvalence exceptionnelle", "Fiabilité légendaire"],
      specs: {
        gamme: "U Series, C Series, CF Series",
        tailles: "Droits 121-131cm, Queues 153-275cm",
        caracteristiques: "Technologie exclusive Yamaha",
        finitions: "Ébène poli, Acajou, Blanc brillant, Noyer",
        origine: "Japon",
        reputation: "Choix des plus grandes salles de concert"
      }
    }
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Pianos <span className="heading-accent">Acoustiques</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              L'excellence de l'artisanat traditionnel. Pianos à queue et droits 
              des plus prestigieuses manufactures mondiales.
            </p>
          </div>
        </div>
      </section>

      {/* Section Pianos Droits */}
      <section className="py-20 bg-gradient-to-b from-transparent via-gray-900/30 to-transparent">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef3 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h2 className="text-4xl font-light text-center text-white mb-6">
              Pianos <span className="heading-accent">Droits</span>
            </h2>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-12"></div>
            
            {/* Avantages Pianos Droits */}
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: Volume2, title: "Bonne Acoustique", desc: "Son naturel authentique" },
                { icon: Music2, title: "Différente Sonorité", desc: "Variété de timbres" },
                { icon: Hand, title: "Précision du Toucher", desc: "Sensibilité exceptionnelle" },
                { icon: Clock, title: "Longévité", desc: "Durabilité de l'instrument" }
              ].map((feature, index) => (
                <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                  <feature.icon className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 font-light text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Pianos à Queue */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal mb-20"
          >
            <h2 className="text-4xl font-light text-center text-white mb-6">
              Pianos <span className="heading-accent">à Queue</span>
            </h2>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-12"></div>
            
            {/* Avantages Pianos à Queue */}
            <div className="grid md:grid-cols-4 gap-6 mb-16">
              {[
                { icon: Volume2, title: "Bon Volume Sonore", desc: "Selon la taille" },
                { icon: Sparkles, title: "Beauté de l'Instrument", desc: "Esthétique exceptionnelle" },
                { icon: Users, title: "Usage Mixte", desc: "Usage privé ou concert" },
                { icon: Award, title: "Pour Confirmés", desc: "Convient davantage aux pianistes confirmés et pro" }
              ].map((feature, index) => (
                <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                  <feature.icon className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 font-light text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* Collection Pianos à Queue */}
            <div className="grid lg:grid-cols-3 gap-8">
              {pianos.map((piano, index) => (
                <div key={`queue-${index}`} className="glass-card interactive-card rounded-2xl overflow-hidden light-effect">
                  <div className="relative h-64">
                    <img
                      src={piano.image}
                      alt={piano.brand}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div className="absolute top-4 right-4">
                      <span className="text-sm font-light text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/30">
                        Prix sur devis
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">{piano.brand}</h3>
                    
                    <div className="space-y-2 mb-6">
                      {piano.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-white/70">
                          <Sparkles className="w-3 h-3 text-yellow-400 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Autres marques disponibles */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef4 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal glass-card rounded-3xl p-8 max-w-4xl mx-auto text-center"
          >
            <p className="text-white/70 font-light text-lg leading-relaxed">
              <span className="text-yellow-400 font-semibold">D'autres marques prestigieuses</span> sont également disponibles en magasin. 
              N'hésitez pas à nous consulter pour découvrir l'ensemble de notre sélection et trouver le piano acoustique qui vous correspond parfaitement.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto text-center">
            <Piano className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
            <h3 className="text-3xl font-light text-white mb-6">
              L'Excellence <span className="heading-accent">Acoustique</span>
            </h3>
            <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
              Chaque piano acoustique est une œuvre d'art. Venez découvrir 
              ces instruments d'exception dans notre showroom alsacien.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setShowAppointmentModal(true)}
                className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide"
              >
                Prendre Rendez-vous
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal des détails */}
      {selectedPiano && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">{selectedPiano.brand}</h3>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <img 
                src={selectedPiano.image} 
                alt={selectedPiano.brand}
                className="w-full h-48 object-cover rounded-2xl mb-6"
              />
              
              <h4 className="text-lg font-semibold text-white mb-4">Caractéristiques Techniques</h4>
              
              <div className="space-y-4">
                {Object.entries(selectedPiano.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-start py-2 border-b border-white/10">
                    <span className="text-white/70 font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-white text-right max-w-xs">{value}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex gap-4">
                <button 
                  onClick={() => {
                    setSelectedPiano(null);
                    scrollToContact();
                  }}
                  className="btn-premium px-6 py-3 rounded-lg font-medium flex-1"
                >
                  Contactez-nous
                </button>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-6 py-3 rounded-lg font-light transition-all duration-300 flex-1"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de prise de rendez-vous */}
      {showAppointmentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-md w-full">
            <div className="p-8 text-center">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">Prendre Rendez-vous</h3>
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="glass-card rounded-2xl p-6">
                  <Phone className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Appelez-nous</h4>
                  <div className="space-y-2 text-white/70">
                    <p>Magasin : <a href="tel:+33389542003" className="text-yellow-400 hover:text-yellow-300 transition-colors">03 89 54 20 03</a></p>
                    <p>Mobile : <a href="tel:+33607971604" className="text-yellow-400 hover:text-yellow-300 transition-colors">06 07 97 16 04</a></p>
                  </div>
                </div>
                
                <div className="glass-card rounded-2xl p-6">
                  <Mail className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Écrivez-nous</h4>
                  <p className="text-white/70">
                    <a href="mailto:musique.buchta@gmail.com" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                      musique.buchta@gmail.com
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="mt-8">
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-8 py-3 rounded-full font-light transition-all duration-300 w-full"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PianoQueueDroitNeufs;